import { useMemo, useState, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { createQueryClient } from "../lib/query-client.ts";
import { AuthProvider, useAuth } from "../auth/index.ts";
import { IdentityProvider, type IdentityState, type RoleState } from "../state/identity.tsx";
import { TenantEnvProvider, type TeamOption } from "../state/tenant-env.tsx";
import { TimeRangeProvider } from "../state/time-range.tsx";
import { DataQualityProvider } from "../state/data-quality.tsx";
import { LiveStreamProvider } from "../state/live-stream.tsx";
import { StreamViewProvider } from "../state/stream-view.tsx";
import { ThemeProvider } from "../state/theme.tsx";

/**
 * Composes every app-wide provider in one place, in a deliberate order:
 *
 * - Router outermost, so navigation (and the command palette's `useNavigate`) is available
 *   everywhere.
 * - QueryClient for request/response data (design 12.1); the client is created once per app with
 *   `useState` so hot-reload or a re-render does not throw the cache away.
 * - AuthProvider (controlplane-auth) sits just inside the QueryClient so it can run its
 *   server-session query, and ABOVE the app state + shell so both the public `/login` route and the
 *   protected shell can read the same session via `useAuth`. It derives its state from a server read
 *   (never a token), so mounting it here does not gate the shell — the shell still loads with no
 *   session; only `/api/*` requires one (A5.2). It never fetches `/api/*`, so a cold core (NFR-7
 *   AC1) is unaffected.
 * - IdentityBridge resolves the operator's role + tenant from the authenticated session and feeds
 *   them into the IdentityProvider the telemetry screens read (`useIdentity`). This is the bridge the
 *   old `identity.tsx` bootstrap seam was left open for: once first-party auth resolves `(role,
 *   tenant)` from `GET /auth/session`, the screens see the real role instead of the honest-but-stale
 *   `unknown`. Before the session settles (or when signed out) the role stays `unknown` (least
 *   privilege); the shell is only reached behind ProtectedRoute anyway, and the server enforces
 *   regardless.
 * - TenantEnv, TimeRange, DataQuality, LiveStream, StreamView: the shared app state the chrome and
 *   the screens read. The live-stream store holds the connection state; the stream-view store holds
 *   the live view, buffered count and gap-disclosed flag. The providers themselves never open a
 *   socket (the driver is mounted in `App`, so it can gate on the cold-core state).
 *
 * The providers never fetch `/api/*` — the shell must load even when `core` is cold (NFR-7 AC1).
 */
export function Providers(props: { children: ReactNode }): ReactNode {
  const [queryClient] = useState(() => createQueryClient());

  return (
    <BrowserRouter>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <IdentityBridge>{props.children}</IdentityBridge>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

/**
 * Bridges the authenticated SESSION identity (`useAuth`, server-derived) into the `IdentityProvider`
 * the telemetry screens read, and seeds the tenant switcher's available tenants from the operator's
 * resolved team memberships. Must render inside `AuthProvider` (it calls `useAuth`). A null/loading
 * identity maps to `role: "unknown"` (least privilege) and no tenant.
 */
function IdentityBridge(props: { children: ReactNode }): ReactNode {
  const auth = useAuth();

  const identity = useMemo<IdentityState>(() => {
    const role: RoleState = auth.identity?.role ?? "unknown";
    const tenant = auth.identity?.team?.id ?? null;
    return { role, tenant };
  }, [auth.identity]);

  // Seed the team switcher with id + ORG DISPLAY NAME options (the server session already carries a
  // per-team `name`); the switcher shows the name and submits the id. Falls back to the id when the
  // server provides no name, so a team is never unlabelled.
  const availableTeams = useMemo<readonly TeamOption[]>(() => {
    const teams = auth.identity?.teams ?? [];
    return teams.map((t) => ({ id: t.id, name: (t.name ?? "").trim() || t.id }));
  }, [auth.identity]);

  return (
    <IdentityProvider value={identity}>
      <TenantEnvProvider availableTeams={availableTeams}>
        <TimeRangeProvider>
          <DataQualityProvider>
            <LiveStreamProvider>
              <StreamViewProvider>{props.children}</StreamViewProvider>
            </LiveStreamProvider>
          </DataQualityProvider>
        </TimeRangeProvider>
      </TenantEnvProvider>
    </IdentityProvider>
  );
}
